/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

const SCALING_FACTOR_DIFFERENCE = 3;

const scales = ['none', 'thousand', 'million', 'billion', 'trillion', 'quadrillion'] as const;

const scalesOrder = scales.map((_, i) => ({ scaleFactor: i * SCALING_FACTOR_DIFFERENCE }));

export type Scale = (typeof scales)[number];

export function getLinearAxisValueFormatter(
  minValue: number,
  maxValue: number,
  tickStep: number,
  locale: string
) {
  const scaleFactor = getScaleFactor(minValue, maxValue);
  const decimalPlaces = getDecimalPlaces(minValue, maxValue, tickStep, scaleFactor);

  const scale = Math.pow(10, scaleFactor);
  const scaleConverterOptions = {
    style: 'decimal' as const,
    notation: 'compact' as const,
    numberingSystem: 'latn' as const,
    useGrouping: false
  };
  let defaultConverter = new Intl.NumberFormat(locale, scaleConverterOptions);

  // Formatting for scale
  const _SCALE_REGEXP = /(\d+)(.*$)/;
  const formattedScale = defaultConverter.format(scale);
  const formattedScaleParts = _SCALE_REGEXP.exec(formattedScale);

  const numberConverterOptions = {
    style: 'decimal' as const,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  };
  defaultConverter = new Intl.NumberFormat(locale, numberConverterOptions);

  return (value: number) => {
    if (!formattedScaleParts) {
      return '';
    }
    const suffix = formattedScaleParts[2]; // Reset the suffix
    const fsn = (Number(formattedScaleParts[1]) / scale) * value;
    let formattedScaledNumber = fsn.toString();
    formattedScaledNumber = defaultConverter.format(fsn);

    // Add the scale factor suffix, unless value is zero
    if (typeof suffix === 'string' && value !== 0) {
      formattedScaledNumber += suffix;
    }
    return formattedScaledNumber;
  };
}

/**
 * Returns the number of decimal places to use for given minValue, maxValue, tickStep, scaleFactor.
 * @param minValue The minValue of the axis.
 * @param maxValue The maxValue of the axis.
 * @param tickStep The step of the axis.
 * @param scaleFactor The scale factor of the scaling of the axis.
 * @returns
 */
function getDecimalPlaces(
  minValue: number,
  maxValue: number,
  tickStep: number,
  scaleFactor: number
) {
  let decimalPlaces;
  if (tickStep === 0 && minValue === maxValue) {
    const valuePowerOfTen = getPowerOfTen(maxValue);
    const scaleFactorDiff = scaleFactor - valuePowerOfTen;
    if (scaleFactorDiff <= 0) {
      // Value is same or larger than the scale factor, ensure 4 significant digits.
      // Make sure that the number of decimal places is at least zero. Bug 18677330
      decimalPlaces = Math.max(scaleFactorDiff + 3, 0);
    } else {
      // Value is smaller, ensure enough decimals to show 1 significant digit
      decimalPlaces = Math.max(scaleFactorDiff, 4);
    }
  } else {
    // get the number of decimal places in the number by subtracting
    // the order of magnitude of the tick step from the order of magnitude
    // of the scale factor
    // (e.g.: scale to K, tick step of 50 -> 3 - 1 = 2 decimal places)
    const tickStepPowerOfTen = getPowerOfTen(tickStep);
    decimalPlaces = Math.max(scaleFactor - tickStepPowerOfTen, 0);
  }

  return decimalPlaces;
}

/**
 * Returns scale factor of scale given by scale name.
 * @param {string} scaleName
 * @return {number} scale factor of scale given by scale name
 */
function getScaleFactor(minValue: number, maxValue: number) {
  const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
  const power = getPowerOfTen(absMax);
  return findNearestLEScaleFactor(power);
}

/**
 * Returns the log base 10 for a given value.
 * @param {number} value
 * @returns
 */
function getPowerOfTen(value: number) {
  // more comprehensive and easier than working with
  // value returned by Math.log(value)/Math.log(10)
  let val = value >= 0 ? value : -value;
  let power = 0;

  // Check for degenerate and zero values
  if (val < 1e-15) {
    return 0;
  } else if (val === Infinity) {
    return Number.MAX_VALUE;
  }

  if (val >= 10) {
    // e.g. for 1000 the power should be 3
    while (val >= 10) {
      power += 1;
      val /= 10;
    }
  } else if (val < 1) {
    while (val < 1) {
      power -= 1;
      val *= 10;
    }
  }
  return power;
}

/**
 * Finds a scale factor 'x' such that x <= value (e.g. if value equals 4 then returned scale factor equals 3)
 * @param {number} value value representing an order of magnitude
 * @return {number} a scale factor 'x' such that x <= value
 */
function findNearestLEScaleFactor(value: number) {
  let scaleFactor = 0;

  if (value <= scalesOrder[0].scaleFactor) {
    // if the number is less than 10, don't scale
    scaleFactor = scalesOrder[0].scaleFactor;
  } else if (value >= scalesOrder[scalesOrder.length - 1].scaleFactor) {
    // if the data is greater than or equal to 10 quadrillion, scale to quadrillions
    scaleFactor = scalesOrder[scalesOrder.length - 1].scaleFactor;
  } else {
    // else find the nearest scaleFactor such that scaleFactor <= value
    const end = scalesOrder.length - 1;
    for (let i = end; i >= 0; i--) {
      if (scalesOrder[i].scaleFactor <= value) {
        scaleFactor = scalesOrder[i].scaleFactor;
        break;
      }
    }
  }
  return scaleFactor;
}
